import {message} from "antd";
import React, {createContext, useContext, useEffect, useRef, useState} from "react";
import {AppContextContext, AttributeDefinitionServiceContext, SbomComponentServiceContext, SbomProjectServiceContext} from "../../Contexts";
import AttributeDefinition from "../../domain/AttributeDefinition";
import {FindingSeverity, getEqualOrGreaterSeverities} from "../../domain/FindingSeverity";
import {Permission} from "../../domain/auth/Permission";
import SbomComponent from "../../domain/SbomComponent";
import SbomComponentFinding from "../../domain/SbomComponentFinding";
import SbomProject from "../../domain/SbomProject";
import SbomProjectVersion from "../../domain/SbomProjectVersion";
import QueryOptions from "../../sal-ui/QueryOptions";
import SingleResult from "../../service/SingleResult";
import FocusedFindingModal from "./FocusedFindingModal";
import UsageGraphModal from "./UsageGraphModal";
import {useFocusedFindingAnalysis} from "./useFocusedFindingAnalysis";

const focusedComponentOptionLoadMoreThresholdPx = 24;
const focusedAnalysisPageSize = 25;
const focusedNavigationComponentsOrderBy = "name asc, id asc";
const vulnerableComponentsFixedRqbQuery = {
    combinator: "and",
    rules: [{field: "severity", operator: "in", value: getEqualOrGreaterSeverities(FindingSeverity.UNKNOWN)}]
};

interface OpenProjectVersionAnalysisOptions {
    projectVersion: SbomProjectVersion;
    sbomProject?: SingleResult<SbomProject> | SbomProject;
    onAfterFindingStatusSave?: (finding: SbomComponentFinding) => void;
}

interface PendingFocus {
    projectVersionId: string;
    component: SbomComponent;
}

interface FocusedFindingAnalysisLauncher {
    openProjectVersionAnalysis: (options: OpenProjectVersionAnalysisOptions) => void;
    isOpeningProjectVersionAnalysis: (projectVersion?: SbomProjectVersion) => boolean;
}

const FocusedFindingAnalysisContext = createContext<FocusedFindingAnalysisLauncher | undefined>(undefined);

export function useFocusedFindingAnalysisLauncher() {
    const launcher = useContext(FocusedFindingAnalysisContext);

    if (launcher === undefined) {
        throw new Error("useFocusedFindingAnalysisLauncher must be used within FocusedFindingAnalysisHost.");
    }

    return launcher;
}

export function FocusedFindingAnalysisHost({children}: { children: React.ReactNode }) {
    const appContext = useContext(AppContextContext);
    const attributeDefinitionService = useContext(AttributeDefinitionServiceContext);
    const sbomComponentService = useContext(SbomComponentServiceContext);
    const sbomProjectService = useContext(SbomProjectServiceContext);
    const openProjectVersionRequestRef = useRef(0);
    const sbomProjectRequestRef = useRef(0);
    const [projectVersionId, setProjectVersionId] = useState<string>();
    const [projectVersion, setProjectVersion] = useState<SbomProjectVersion>();
    const [sbomProject, setSbomProject] = useState<SingleResult<SbomProject>>();
    const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinition[]>();
    const [showAiAnalytics, setShowAiAnalytics] = useState(true);
    const [pendingFocus, setPendingFocus] = useState<PendingFocus>();
    const [openingProjectVersionId, setOpeningProjectVersionId] = useState<string>();
    const [onAfterFindingStatusSave, setOnAfterFindingStatusSave] = useState<((finding: SbomComponentFinding) => void)>();
    const [usageModalOpen, setUsageModalOpen] = useState(false);
    const [usageGraphData, setUsageGraphData] = useState<any>();
    const [usageModalTitle, setUsageModalTitle] = useState("");
    const focusedAnalysisScope = useFocusedFindingAnalysis({
        projectVersionId,
        componentFindingsPageSize: focusedAnalysisPageSize,
        findingOptionPageSize: focusedAnalysisPageSize,
        navigationComponentsPageSize: focusedAnalysisPageSize,
        componentOptionLoadMoreThresholdPx: focusedComponentOptionLoadMoreThresholdPx,
        navigationComponentsOrderBy: focusedNavigationComponentsOrderBy,
        vulnerableComponentsRqbQuery: vulnerableComponentsFixedRqbQuery
    });
    const focusedAnalysis = focusedAnalysisScope.controller;
    const focusedAnalysisRef = useRef(focusedAnalysis);

    useEffect(() => {
        focusedAnalysisRef.current = focusedAnalysis;
    });

    useEffect(() => {
        if (pendingFocus === undefined || pendingFocus.projectVersionId !== projectVersionId) {
            return;
        }

        focusedAnalysisRef.current.focusComponent(pendingFocus.component);
        setPendingFocus(undefined);
    }, [pendingFocus, projectVersionId]);

    const launcher = {
        openProjectVersionAnalysis,
        isOpeningProjectVersionAnalysis
    };

    return <FocusedFindingAnalysisContext.Provider value={launcher}>
        {children}

        <UsageGraphModal
            open={usageModalOpen}
            title={usageModalTitle}
            graphData={usageGraphData}
            onClose={() => setUsageModalOpen(false)}
        />

        <FocusedFindingModal
            analysis={focusedAnalysis}
            sbomProject={sbomProject}
            projectVersion={projectVersion}
            attributeDefinitions={attributeDefinitions}
            showAiAnalytics={showAiAnalytics}
            canOpenFinding={canOpenFinding()}
            onShowUsagesClick={onShowUsagesClick}
            onFindingStatusSave={onFindingStatusSave}
            onShowAiAnalyticsChange={setShowAiAnalytics}
        />
    </FocusedFindingAnalysisContext.Provider>;

    function openProjectVersionAnalysis(options: OpenProjectVersionAnalysisOptions) {
        const targetProjectVersionId = options.projectVersion.id;

        if (targetProjectVersionId === undefined) {
            return;
        }

        const requestId = openProjectVersionRequestRef.current + 1;

        openProjectVersionRequestRef.current = requestId;

        focusedAnalysis.close();
        setPendingFocus(undefined);
        setUsageModalOpen(false);
        setOpeningProjectVersionId(targetProjectVersionId);
        setProjectVersionId(targetProjectVersionId);
        setProjectVersion(options.projectVersion);
        loadSbomProjectContext(options.sbomProject ?? options.projectVersion.project);
        setOnAfterFindingStatusSave(() => options.onAfterFindingStatusSave);
        loadAttributeDefinitionsIfNeeded();

        const queryOptions = new QueryOptions(
            focusedNavigationComponentsOrderBy,
            1,
            0,
            undefined,
            undefined,
            vulnerableComponentsFixedRqbQuery
        );

        sbomComponentService.getList(targetProjectVersionId, queryOptions)
            .then(components => {
                if (openProjectVersionRequestRef.current !== requestId) {
                    return;
                }

                const component = components.data[0];

                if (!component?.id) {
                    message.info("No vulnerability findings available for this project version.");

                    return;
                }

                setPendingFocus({projectVersionId: targetProjectVersionId, component});
            })
            .catch(() => {
                if (openProjectVersionRequestRef.current === requestId) {
                    message.error("Cannot open vulnerability analysis.");
                }
            })
            .finally(() => {
                if (openProjectVersionRequestRef.current === requestId) {
                    setOpeningProjectVersionId(undefined);
                }
            });
    }

    function isOpeningProjectVersionAnalysis(projectVersion?: SbomProjectVersion) {
        return projectVersion?.id !== undefined && projectVersion.id === openingProjectVersionId;
    }

    function loadAttributeDefinitionsIfNeeded() {
        if (attributeDefinitions !== undefined) {
            return;
        }

        attributeDefinitionService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setAttributeDefinitions(value.data));
    }

    function loadSbomProjectContext(sbomProject?: SingleResult<SbomProject> | SbomProject) {
        const requestId = sbomProjectRequestRef.current + 1;

        sbomProjectRequestRef.current = requestId;

        if (sbomProject instanceof SingleResult || sbomProject === undefined) {
            setSbomProject(sbomProject);

            return;
        }

        setSbomProject(new SingleResult(sbomProject));

        if (sbomProject.id === undefined) {
            return;
        }

        sbomProjectService.get(sbomProject.id)
            .then(result => {
                if (sbomProjectRequestRef.current === requestId) {
                    setSbomProject(result);
                }
            })
            .catch(() => undefined);
    }

    function canOpenFinding() {
        return appContext.user.hasPermission(Permission.SBOM_PROJECT__MANAGE_ORG)
            || sbomProject?.permissions?.includes(Permission.SBOM_PROJECT__ANALYZE)
            || sbomProject?.permissions?.includes(Permission.SBOM_PROJECT__MANAGE);
    }

    function onShowUsagesClick(sbomComponent: SbomComponent) {
        sbomComponentService.getUnovisGraph(sbomComponent.id)
            .then(response => {
                if (response.status === 204) {
                    message.info("Usage graph is not available for this component.");

                    return;
                }

                setUsageModalTitle(`Usage of ${sbomComponent.bomRef}`);
                setUsageGraphData(response.data);
                setUsageModalOpen(true);
            })
            .catch(() => message.error("Cannot retrieve component usage graph."));
    }

    function onFindingStatusSave(finding: SbomComponentFinding) {
        const findingsPage = focusedAnalysis.componentsFindings[finding.component.id];

        onAfterFindingStatusSave?.(finding);
        focusedAnalysis.reloadComponentFindings(finding.component.id, findingsPage?.skip ?? 0, findingsPage?.sort);
        focusedAnalysis.reloadNavigationIfModalOpen();
    }
}
